import { ResponsiveLine } from '@nivo/line';
import React, { useCallback, useState, useEffect } from 'react';
import {
  getFormattedValue,
  BLUE_PLOOT,
  TEXT_BLACK_1,
  DRAK_GREEN_PLOOT,
  DARK_RED_PLOOT,
} from '@plootus/common';
import styles from './LineChart.module.css';
import useWindowDimensions from '../../../Reusables/VisualAndAccord/Hooks/useWindowDimensions';
import moment from 'moment';

const MyResponsiveLine = ({ data, pickerIndex, small }) => {
  const toolTipElement = (props) => {
    const date = props.point.data.x.toString();
    const fdate = moment(date).format('DD MMM YYYY');
    const val = Number(props.point.data.y) || 0;
    const moneyColor = val < 0 ? DARK_RED_PLOOT : DRAK_GREEN_PLOOT;
    return (
      <div className={small ? styles.smalltoolTip : styles.toolTip}>
        Date:
        <span className={styles.xVal}> {fdate}</span>
        <div className={styles.yVal}>
          Net worth:{' '}
          <span
            className={small ? styles.smallmoney : styles.money}
            style={{ color: moneyColor }}
          >
            {getFormattedValue(props.point.data.y, 'money', 2)}
          </span>
        </div>
      </div>
    );
  };

  const { width } = useWindowDimensions();
  const newData = [
    {
      data: data,
    },
  ];

  const dummy = data.map((item) => {
    return {
      x: item.x,
      y: 0,
    };
  });
  const dummyData = [
    {
      data: dummy,
    },
  ];

  const [graphData, setGraphData] = useState(dummyData);

  const getStep = (val) => {
    const n = Math.abs(val);
    if (n < 1000) return 100;
    if (n < 5000) return 500;
    if (n < 10000) return 1000;
    if (n < 50000) return 5000;
    if (n < 100000) return 10000;
    if (n < 500000) return 25000;
    return 50000;
  };

  const getMax = () => {
    let max = -Infinity;
    data.forEach((item) => {
      max = Math.max(max, Number(item.y) || 0);
    });
    let min = Infinity;
    data.forEach((item) => {
      min = Math.min(min, Number(item.y) || 0);
    });
    const span = Math.max(Math.abs(max), Math.abs(min));
    const step = getStep(span);
    if (max <= 0) return 0;
    const padded = max * 1.1;
    return Math.ceil(padded / step) * step;
  };

  const getMin = () => {
    let min = Infinity;
    data.forEach((item) => {
      min = Math.min(min, Number(item.y) || 0);
    });
    let max = -Infinity;
    data.forEach((item) => {
      max = Math.max(max, Number(item.y) || 0);
    });
    if (min >= 0) return 0;
    const span = Math.max(Math.abs(max), Math.abs(min));
    const step = getStep(span);
    const padded = min * 1.1;
    return Math.floor(padded / step) * step;
  };

  useEffect(() => {
    setTimeout(() => {
      setGraphData(newData);
    }, 100);
  }, []);

  const chartTheme = () => {
    return {
      grid: {
        line: {
          stroke: 'rgba(0,0,0,0.05)',
        },
      },
      axis: {
        legend: {
          text: {
            fill: BLUE_PLOOT,
            fontSize: width > 576 ? 15 : 12,
          },
        },
        ticks: {
          text: {
            fill: TEXT_BLACK_1,
            fontSize: width > 576 ? 12 : 9,
            fontFamily: 'Arial',
          },
          line: {
            stroke: 'rgba(0,0,0,0.3)',
            strokeWidth: 1,
          },
        },
        domain: {
          line: {
            stroke: 'rgba(0,0,0,0.1)',
            strokeWidth: 1,
          },
        },
      },
      crosshair: {
        line: {
          stroke: 'rgb(0,0,0)',
          strokeWidth: 1,
          strokeOpacity: 1,
        },
      },
    };
  };
  const maxY = getMax();
  const minY = getMin();
  const margins = {
    top: width > 576 ? (small ? 20 : 60) : 40,
    right: width > 576 ? (small ? 40 : 60) : 20,
    bottom:
      minY < 0
        ? small
          ? 60
          : width > 576
          ? 100
          : 60
        : small
        ? 50
        : width > 576
        ? 80
        : 40,
    left: width > 576 ? 100 : 60,
  };

  return data?.length ? (
    <div className={styles.chartCont}>
      <ResponsiveLine
        data={newData}
        margin={margins}
        xScale={{
          type: 'time',
          format: '%Y-%m-%d',
          useUTC: false,
          precision: 'day',
        }}
        xFormat="time:%Y-%m-%d"
        yScale={{
          type: 'linear',
          min: minY,
          max: maxY,
          stacked: false,
          reverse: false,
        }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          orient: 'bottom',
          tickSize: 5,
          tickPadding: minY < 0 ? 10 : 5,
          tickRotation: 0,
          format: '%d %b %Y',
          tickValues:
            small || width < 576 ? 'every 3 months' : 'every 2 months',
        }}
        axisLeft={{
          orient: 'left',
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          tickValues: 5,
          format: (d) => `${getFormattedValue(d, 'money', 2)}`,
        }}
        theme={chartTheme()}
        colors={[BLUE_PLOOT]}
        pointSize={0}
        pointColor={{ theme: 'background' }}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        pointLabel="y"
        pointLabelYOffset={-12}
        useMesh={true}
        enableArea
        areaBaselineValue={0}
        areaOpacity={0.35}
        curve={'catmullRom'}
        tooltip={toolTipElement}
        animate={true}
        motionStiffness={50}
        crosshairType="cross"
        markers={[
          {
            axis: 'y',
            value: 0,
            lineStyle: {
              stroke: 'rgba(0,0,0,0.6)',
              strokeWidth: 1,
              strokeDasharray: '6, 6',
            },
          },
        ]}
      />
    </div>
  ) : null;
};

export default MyResponsiveLine;
